// @flow

import get from 'lodash/get';
import has from 'lodash/has';
import memoize from 'memoize-one';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
  updateProduct,
  selectOneProductById,
  selectCartIsWorking,
} from '@gandi/cart';

// @TODO: don't import from a private folder.
// eslint-disable-next-line @gandi/gandi/no-private-import
import type { ContactPayload } from '@gandi/cart/dist/types';

import {
  type FormValues,
  fetchTldInfo,
  CONTACTS_FORM,
  maybeConvertType,
} from '@gandi/domain-configuration';
import { notifyDanger } from '@gandi/notifications';
import type { FetchResponse } from '@gandi/resource/redux';
import { withTranslator } from '@gandi/translate';

import {
  withForm,
  SubmissionError,
  selectRegisteredFieldsValues,
  isSubmitting,
  authorizeHtml,
} from 'gandi.react-form';
import { POST } from 'gandi.redux-fetcher';
import { withLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';

import type { FullState, Dispatch } from '../../../types';

import DomainContactEditionPresenter, {
  type Props,
  type InjectedProps,
  type DispatchProps,
} from './components/Presenter';

/**
 * Validate the contact,
 * and update the product or show errors.
 */

export const onSubmit = async (
  formValues: FormValues,
  dispatch: Dispatch,
  props: Props,
): Promise<void> => {
  const {
    logger,
    translator: { __ },
    params: { productId, role },
    backUrl,
    fqdn,
    product,
    navigate,
  } = props;

  if (!product) {
    return Promise.resolve();
  }
  const body = {
    ...props.contactValues,
    roles: [role],
  };

  const url = `/contactvalidation/validfor/${fqdn}`;

  // Validate Values
  try {
    await dispatch(
      POST(url, body, {
        // $FlowFixMe[incompatible-call]
        label: `validateRole/${fqdn}`,
        // $FlowFixMe[incompatible-call]
        fqdn,
        // $FlowFixMe[incompatible-call]
        role,
        // $FlowFixMe[prop-missing]
        values: props.contactValues,
        // $FlowFixMe[incompatible-exact]
        on400: (response: FetchResponse<>) => Promise.reject(response),
        // $FlowFixMe[incompatible-exact]
        on500: (err: FetchResponse<>) => Promise.reject(err),
      }),
    );
  } catch (fetchError) {
    // Server Error e.g. 500 Server Error
    if (fetchError && fetchError.status !== 400) {
      return dispatch(
        notifyDanger({
          message: __('An error has occurred. Please try again.'),
        }),
      );
    }
    // Validation Error e.g. 400 phone is missing
    let errors;
    if (has(fetchError, 'data.errors')) {
      // map errors to the form expected format
      errors = get(fetchError, 'data.errors', []).reduce((acc, error) => {
        if (error.name.indexOf('extra_parameters') > -1) {
          const errorKey = error.name.split('.')[1];
          return {
            ...acc,
            extra_parameters: {
              ...acc.extra_parameters,
              [errorKey]: error.description,
            },
          };
        }
        return {
          ...acc,
          [error.name]: error.description,
        };
      }, {});

      throw new SubmissionError(authorizeHtml(errors));
    } else {
      const message = get(
        fetchError,
        'message',
        __('An error has occurred. Please try again.'),
      );
      dispatch(notifyDanger({ message }));
    }
  }
  // if the data is valid we update the product payload
  try {
    const newPayload = {
      ...product.payload,
      [role]: props.contactValues,
    };
    // $FlowFixMe[incompatible-indexer] TODO: Fix type
    await dispatch(updateProduct({ productId, payload: newPayload }));
  } catch (err) {
    dispatch(logger.error({ err }, 'Unable to update the product.'));
    dispatch(
      notifyDanger({
        message: __('An error has occurred. Please try again.'),
      }),
    );
  }

  navigate(backUrl);
  return undefined;
};

const mapDispatchToProps: DispatchProps = {
  fetchTldInfo,
  notifyDanger,
};

const EMPTY_OBJECT = {};

// FIXME: To keep as long as legacy type is used in `@gandi/domain-configuration` `<ContactForm>`
const getContactWithLegacyRole = memoize((contact: ContactPayload) => ({
  ...contact,
  type: maybeConvertType(contact?.type),
}));

const mapStateToProps = (
  state: FullState,
  ownProps: InjectedProps,
): Partial<Props> => {
  const {
    params: { cartId, productId, role },
  } = ownProps;

  const product = selectOneProductById(state, { productId });
  const contactValues: FormValues =
    selectRegisteredFieldsValues(state, {
      form: CONTACTS_FORM,
    }) || {};

  return {
    isSubmitting: isSubmitting(CONTACTS_FORM)(state),
    fqdn: get(product, 'label'),
    phase: get(product, 'payload.tld_phase'),
    process: get(product, 'process'),
    contact:
      (product?.payload?.[role] &&
        getContactWithLegacyRole(product?.payload?.[role])) ||
      EMPTY_OBJECT,
    backUrl: `/cart/${cartId}/overview`,
    cartIsWorking: selectCartIsWorking(state),
    product,
    // Ignore (initial) values of non-registered fields.
    // (previous extra-params such as VAT/siren or orgName)
    contactValues,
  };
};

const Component: React.ComponentType<{}> = compose(
  withTranslator({ propsNamespace: 'translator' }),
  withLogger('Configure/DomainContacts/DomainContactEdition'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withForm({
    form: CONTACTS_FORM,
    propNamespace: 'form',
    onSubmit,
  }),
)(DomainContactEditionPresenter);

export default Component;
